import { defineStore } from 'pinia';
import { ref } from 'vue';

/**
 * Shape of the icon manifest JSON fetched from the CDN.
 */
interface IconManifest {
  generatedAt: string;
  iconCount: number;
  icons: Array<{
    name: string;
    file: string;
    aliases: string[];
  }>;
  aliases: Record<string, string>;
  missingAliases: Record<string, string>;
}

const cdnUrl = import.meta.env.VITE_CDN_URL ?? 'https://cdn.econnect.eu';
const iconsVersion = import.meta.env.VITE_ICONS_VERSION ?? '1.0.0.0';

/**
 * Builds an {@link IconManifest} object from the current store state.
 */
const buildManifestFromState = (params: {
  generatedAt: string | null;
  iconCount: number;
  icons: IconManifest['icons'];
  aliases: IconManifest['aliases'];
  missingAliases: IconManifest['missingAliases'];
}): IconManifest => {
  const { generatedAt, iconCount, icons, aliases, missingAliases } = params;

  return {
    generatedAt: generatedAt ?? '',
    iconCount,
    icons,
    aliases,
    missingAliases
  };
};

/**
 * Icon metadata and SVG fetching store.
 *
 * Exposes:
 * - Manifest data (icons, aliases, counts, timestamps)
 * - Name/alias resolution
 * - Cached SVG fetching
 */
export const useIconStore = defineStore('icons', () => {
  const icons = ref<IconManifest['icons']>([]);
  const aliases = ref<IconManifest['aliases']>({});
  const missingAliases = ref<IconManifest['missingAliases']>({});
  const iconCount = ref<number>(0);
  const generatedAt = ref<string | null>(null);
  const isLoadingList = ref(false);

  const svgCache = ref<Map<string, string>>(new Map());
  const loadingSvgs = ref<Map<string, Promise<string | null>>>(new Map());

  /**
   * Fetches the icon manifest once and caches it in the store.
   *
   * When a request is already in flight, subsequent calls wait until
   * loading completes and then return the cached data.
   */
  const fetchIconList = async (): Promise<IconManifest | null> => {
    if (icons.value.length > 0) {
      return buildManifestFromState({
        generatedAt: generatedAt.value,
        iconCount: iconCount.value,
        icons: icons.value,
        aliases: aliases.value,
        missingAliases: missingAliases.value
      });
    }

    if (isLoadingList.value) {
      return new Promise<IconManifest | null>(resolve => {
        const checkInterval = globalThis.setInterval(() => {
          if (!isLoadingList.value) {
            clearInterval(checkInterval);
            resolve(
              icons.value.length > 0
                ? buildManifestFromState({
                    generatedAt: generatedAt.value,
                    iconCount: iconCount.value,
                    icons: icons.value,
                    aliases: aliases.value,
                    missingAliases: missingAliases.value
                  })
                : null
            );
          }
        }, 50);
      });
    }

    isLoadingList.value = true;

    try {
      const response = await fetch(`${cdnUrl}/core/icons/${iconsVersion}/manifest.json`);

      if (!response.ok) {
        console.error('CDN manifest request failed:', response.status, response.statusText);
        return null;
      }

      const data: IconManifest = await response.json();

      icons.value = data.icons;
      aliases.value = data.aliases;
      missingAliases.value = data.missingAliases;
      iconCount.value = data.iconCount;
      generatedAt.value = data.generatedAt;

      return data;
    } catch (error) {
      console.error('Error fetching icons from CDN:', error);
      return null;
    } finally {
      isLoadingList.value = false;
    }
  };

  /**
   * Resolves an icon name or alias to the actual icon name.
   *
   * @param nameOrAlias - Raw name or alias to resolve.
   * @returns The concrete icon name, or `null` when unknown.
   */
  const resolveIconName = (nameOrAlias: string): string | null => {
    if (!nameOrAlias) return null;

    const directIcon = icons.value.find(icon => icon.name === nameOrAlias);
    if (directIcon) return nameOrAlias;

    const resolvedName = aliases.value[nameOrAlias];
    if (resolvedName) return resolvedName;

    console.warn(`Icon "${nameOrAlias}" not found in manifest`);
    return null;
  };

  /**
   * Normalizes SVG colors to `currentColor`.
   */
  const processSvgContent = (svgContent: string): string =>
    svgContent
      .replaceAll(/fill="[^"]*"/gi, 'fill="currentColor"')
      .replaceAll(/stroke="[^"]*"/gi, 'stroke="currentColor"');

  /**
   * Fetches SVG content for a specific icon from the CDN.
   *
   * Results are:
   * - cached per icon name, and
   * - deduplicated when multiple calls happen in parallel.
   *
   * @param iconName - Concrete icon name (not an alias).
   * @returns The processed SVG markup, or `null` when unavailable.
   */
  const fetchSvgContent = async (iconName: string): Promise<string | null> => {
    if (svgCache.value.has(iconName)) {
      return svgCache.value.get(iconName)!;
    }

    if (loadingSvgs.value.has(iconName)) {
      return loadingSvgs.value.get(iconName)!;
    }

    const icon = icons.value.find(i => i.name === iconName);
    if (!icon) return null;

    const loadingPromise: Promise<string | null> = (async () => {
      try {
        const svgUrl = `${cdnUrl}/core/icons/${iconsVersion}/${icon.file}`;
        const response = await fetch(svgUrl);

        if (!response.ok) {
          console.error(
            `Failed to fetch icon "${iconName}":`,
            response.status,
            response.statusText
          );
          return null;
        }

        const rawSvgContent = await response.text();
        const processedSvg = processSvgContent(rawSvgContent);
        svgCache.value.set(iconName, processedSvg);
        return processedSvg;
      } catch (error) {
        console.error(`Error fetching icon "${iconName}":`, error);
        return null;
      } finally {
        loadingSvgs.value.delete(iconName);
      }
    })();

    loadingSvgs.value.set(iconName, loadingPromise);
    return loadingPromise;
  };

  return {
    icons,
    aliases,
    missingAliases,
    iconCount,
    generatedAt,
    isLoadingList,
    svgCache,
    fetchIconList,
    resolveIconName,
    fetchSvgContent
  };
});

/**
 * Strongly-typed icon store instance.
 *
 * Usage:
 * ```ts
 * const store: IconStore = useIconStore();
 * ```
 */
export type IconStore = ReturnType<typeof useIconStore>;
